import {
  getAllSearchParams,
  transformJsonapi,
  useApi,
  useEvents,
  type ApiResponseMany,
  type Filters,
} from "@mb-pro-ui/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Cdec } from "./types";

const FIRST_PAGE = 50;
const NEWER_PAGE = 200;
const OLDER_PAGE = 50;
const LIVE_CAP = 300;
const FALLBACK_REFRESH_MS = 20 * 1000;

// GPRS lifesigns are the events of this category (the alarmdb event
// preprocessor's GPRS lifesign branch). Filtered with `nin`, not `ne`: `ne`
// also drops rows that match no code-table event (NULL category).
const GPRS_LIFESIGN_CATEGORY = "29";

const include = { zone: {}, user: {} } as const;
const fields = {
  "cdec-all": [
    "active",
    "arrived",
    "sent",
    "color",
    "event-code",
    "localized-description",
    "zone-number",
    "partition-number",
    "zone",
    "user",
  ],
  zones: ["place"],
  users: ["name"],
} as const;

type StreamResponse = ApiResponseMany & {
  meta?: { cursor?: string | number | null };
};

export type StreamStatus = "loading" | "ok" | "error";

interface StreamState {
  rows: Cdec[];
  status: StreamStatus;
  lastSuccess: number | null;
  hasOlder: boolean;
  loadingOlder: boolean;
}

const initialState: StreamState = {
  rows: [],
  status: "loading",
  lastSuccess: null,
  hasOlder: false,
  loadingOlder: false,
};

const byIdDesc = (a: Cdec, b: Cdec) => Number(b.id) - Number(a.id);

function merge(current: Cdec[], incoming: Cdec[]) {
  const byId = new Map(current.map((row) => [row.id, row]));
  for (const row of incoming) {
    byId.set(row.id, row);
  }
  return [...byId.values()].sort(byIdDesc);
}

function minCursor(a: string | null, b: string | null) {
  if (a === null) return b;
  if (b === null) return a;
  return Number(a) <= Number(b) ? a : b;
}

/**
 * The events of one customer from `alarm/cdec-all` (online and offline
 * tables), newest first, kept current while mounted.
 *
 * Follow-up requests resume from the response's `meta.cursor`, the highest
 * id below which every row is committed, never from the newest id seen:
 * ids are assigned before commit, so a lower id can become visible after a
 * higher one. Rows above the cursor are fetched again and merged by id.
 *
 * Refreshes are single-flight: a trigger during a running refresh makes
 * exactly one more pass after it. Triggers are the org-wide `cdec-insert`
 * poke, socket (re)connects, the page becoming visible and a fallback timer.
 * Offline-table inserts send no poke, so those rows appear on the timer.
 */
export function useCustomerEventStream(customerId: string, hideGprsLifesigns: boolean) {
  const api = useApi();
  const [state, setState] = useState<StreamState>(initialState);

  const filter = useMemo<Filters>(
    () => ({
      customer: { eq: customerId },
      ...(hideGprsLifesigns
        ? { "event-category": { nin: [GPRS_LIFESIGN_CATEGORY] } }
        : {}),
    }),
    [customerId, hideGprsLifesigns],
  );

  // Per-stream mutable state; a new generation (other customer or filter)
  // discards whatever an older one still has in flight.
  const generation = useRef(0);
  const cursor = useRef<string | null>(null);
  const running = useRef(false);
  const dirty = useRef(false);
  const cap = useRef(LIVE_CAP);
  const controller = useRef<AbortController | null>(null);

  const fetchRows = useCallback(
    async (
      extraFilter: Filters,
      sort: string,
      limit: number,
      signal: AbortSignal,
    ) => {
      const { includePaths, searchParams } = getAllSearchParams<Cdec>({
        include,
        fields,
        filter: { ...filter, ...extraFilter },
        sort: [sort],
        page: { limit },
      });
      const response = await api<StreamResponse>(
        `/alarm/cdec-all${searchParams}`,
        { signal },
      );
      const rows = transformJsonapi<ApiResponseMany, Cdec>(
        response,
        includePaths,
      ) as Cdec[];
      const metaCursor = response.meta?.cursor;
      return {
        rows,
        cursor: metaCursor === null || metaCursor === undefined ? null : String(metaCursor),
      };
    },
    [api, filter],
  );

  const pass = useCallback(
    async (gen: number, signal: AbortSignal) => {
      if (cursor.current === null) {
        const first = await fetchRows({}, "-id", FIRST_PAGE, signal);
        if (gen !== generation.current) return;
        cursor.current = first.cursor;
        setState((s) => ({
          ...s,
          rows: merge(s.rows, first.rows),
          hasOlder: s.rows.length === 0 ? first.rows.length === FIRST_PAGE : s.hasOlder,
          status: "ok",
          lastSuccess: Date.now(),
        }));
        return;
      }

      // Oldest first from the cursor, page by page, so a burst larger than
      // a page leaves no hole. The next cursor is the lowest one seen: each
      // page's cursor is only safe for the snapshot it was read in.
      let lower = cursor.current;
      let next: string | null = null;
      const collected: Cdec[] = [];
      for (;;) {
        const page = await fetchRows({ id: { gt: lower } }, "id", NEWER_PAGE, signal);
        if (gen !== generation.current) return;
        collected.push(...page.rows);
        next = minCursor(next, page.cursor);
        if (page.rows.length < NEWER_PAGE) break;
        lower = page.rows[page.rows.length - 1].id;
      }
      if (next !== null) {
        cursor.current = next;
      }
      setState((s) => {
        const rows = merge(s.rows, collected);
        const trimmed = rows.length > cap.current;
        return {
          ...s,
          rows: trimmed ? rows.slice(0, cap.current) : rows,
          hasOlder: s.hasOlder || trimmed,
          status: "ok",
          lastSuccess: Date.now(),
        };
      });
    },
    [fetchRows],
  );

  const refresh = useCallback(async () => {
    if (running.current) {
      dirty.current = true;
      return;
    }
    running.current = true;
    const gen = generation.current;
    const ctrl = new AbortController();
    controller.current = ctrl;
    try {
      do {
        dirty.current = false;
        await pass(gen, ctrl.signal);
      } while (dirty.current && gen === generation.current);
    } catch (error) {
      if (!ctrl.signal.aborted && gen === generation.current) {
        console.warn("event stream refresh failed", error);
        setState((s) => ({ ...s, status: "error" }));
      }
    } finally {
      if (controller.current === ctrl) {
        controller.current = null;
        running.current = false;
      }
    }
  }, [pass]);

  const loadOlder = useCallback(async () => {
    const gen = generation.current;
    const oldest = state.rows[state.rows.length - 1];
    if (!oldest || state.loadingOlder) return;
    setState((s) => ({ ...s, loadingOlder: true }));
    try {
      const page = await fetchRows(
        { id: { lt: oldest.id } },
        "-id",
        OLDER_PAGE,
        new AbortController().signal,
      );
      if (gen !== generation.current) return;
      cap.current += page.rows.length;
      setState((s) => ({
        ...s,
        rows: merge(s.rows, page.rows),
        hasOlder: page.rows.length === OLDER_PAGE,
        loadingOlder: false,
      }));
    } catch (error) {
      if (gen !== generation.current) return;
      console.warn("loading older events failed", error);
      setState((s) => ({ ...s, loadingOlder: false }));
    }
  }, [fetchRows, state.rows, state.loadingOlder]);

  // New stream on customer or filter change.
  useEffect(() => {
    generation.current += 1;
    cursor.current = null;
    cap.current = LIVE_CAP;
    controller.current?.abort();
    controller.current = null;
    running.current = false;
    dirty.current = false;
    setState(initialState);
    void refresh();
    return () => {
      generation.current += 1;
      controller.current?.abort();
    };
  }, [refresh]);

  useEvents(["cdec-insert", "connect", "alarm:scoped"], () => {
    void refresh();
  });

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void refresh();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        void refresh();
      }
    }, FALLBACK_REFRESH_MS);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(timer);
    };
  }, [refresh]);

  return { ...state, refresh, loadOlder };
}
